import React, { useState } from 'react';
import { Save, X, Trash2 } from 'lucide-react';
import { Button, Input } from './ui';
import { HotelWithProgress } from '../types';

interface EditHotelFormProps {
  hotel: HotelWithProgress;
  onSave: (hotelData: {
    name: string;
    contactPerson: string;
    imageUrl: string;
    startDate: string;
  }) => void;
  onCancel: () => void;
  onDelete?: (hotelId: string) => void;
}

export function EditHotelForm({ hotel, onSave, onCancel, onDelete }: EditHotelFormProps) {
  const [formData, setFormData] = useState({
    name: hotel.name,
    contactPerson: hotel.contactPerson,
    imageUrl: hotel.imageUrl,
    startDate: hotel.startDate || new Date().toISOString().split('T')[0],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onSave(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete(hotel.id);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <label htmlFor="hotel-name" className="block text-sm font-medium text-gray-700 mb-2">
            Hotel Name
          </label>
          <Input
            id="hotel-name"
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Enter hotel name"
            required
            className="w-full"
          />
        </div>

        <div>
          <label htmlFor="contact-person" className="block text-sm font-medium text-gray-700 mb-2">
            Contact Person
          </label>
          <Input
            id="contact-person"
            type="text"
            value={formData.contactPerson}
            onChange={(e) => handleChange('contactPerson', e.target.value)}
            placeholder="Enter contact person name"
            required
            className="w-full"
          />
        </div>

        <div>
          <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-2">
            Start Date
          </label>
          <Input
            id="start-date"
            type="date"
            value={formData.startDate}
            onChange={(e) => handleChange('startDate', e.target.value)}
            required
            className="w-full"
          />
        </div>

        <div>
          <label htmlFor="image-url" className="block text-sm font-medium text-gray-700 mb-2">
            Hotel Image URL
          </label>
          <Input
            id="image-url"
            type="url"
            value={formData.imageUrl}
            onChange={(e) => handleChange('imageUrl', e.target.value)}
            placeholder="https://example.com/hotel-image.jpg"
            required
            className="w-full"
          />
        </div>

        {/* Preview */}
        {formData.imageUrl && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Image Preview
            </label>
            <div className="relative h-32 bg-gray-100 rounded-2xl overflow-hidden">
              <img
                src={formData.imageUrl}
                alt="Hotel preview"
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
                }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-4">
        {/* Delete button on the left */}
        {onDelete && (
          <Button
            type="button"
            variant="outline"
            onClick={handleDelete}
            loading={isDeleting}
            className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100 hover:border-red-300"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Hotel
          </Button>
        )}
        
        {/* Save and Cancel buttons on the right */}
        <div className="flex items-center space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="bg-white/40 backdrop-blur-xl border-gray-200"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={!formData.name.trim() || !formData.contactPerson.trim() || !formData.imageUrl.trim()}
          >
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </div>
    </form>
  );
}
